import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, WarningCircle, CaretDown, CaretUp, TrendUp, TrendDown,
  ListChecks, Info, ArrowsLeftRight, ArrowClockwise, Quotes,
} from '@phosphor-icons/react';

const DQ_COLORS = { HIGH: '#10B981', MEDIUM: '#3B82F6', LOW: '#F59E0B', INSUFFICIENT: '#EF4444' };
const CONF_COLORS = { alta: '#10B981', media: '#F59E0B', baixa: '#EF4444', nao_verificado: '#9CA3AF' };
const IMPACT_COLORS = { alto: '#EF4444', medio: '#F59E0B', baixo: '#9CA3AF' };

const prettify = (s) => (s || '').split('.').pop().replace(/_/g, ' ');

export const DataQualityBadge = ({ dq, confidence }) => {
  const { t } = useTranslation();
  if (!dq?.level) return null;
  const c = DQ_COLORS[dq.level] || '#9CA3AF';
  const cc = CONF_COLORS[confidence] || '#9CA3AF';
  return (
    <>
      <span
        className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border"
        style={{ background: `${c}18`, color: c, borderColor: `${c}55` }}
        data-testid="winner-score-dq-badge"
        title={(dq.reasons || []).join(' · ')}
      >
        {t('ws.dq_label')}: {t(`ws.dq_${dq.level}`, { defaultValue: dq.level })}
      </span>
      {confidence && (
        <span
          className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border"
          style={{ background: `${cc}18`, color: cc, borderColor: `${cc}55` }}
          data-testid="winner-score-confidence-badge"
        >
          {t('ws.conf_label')}: {t(`ws.conf_${confidence}`, { defaultValue: confidence })}
        </span>
      )}
    </>
  );
};

export const InsufficientCard = ({ result, onRetry }) => {
  const { t } = useTranslation();
  const dq = result.data_quality || {};
  return (
    <div className="p-8 bg-[#7F1D1D]/10 border border-red-500/40 rounded-3xl" data-testid="winner-score-insufficient">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0">
          <WarningCircle size={26} weight="bold" className="text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-[Outfit] font-bold text-red-100 mb-2">{t('ws.insufficient_title')}</h2>
          <p className="text-white/70 leading-relaxed mb-4">{t('ws.insufficient_desc')}</p>

          {(dq.reasons || []).length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">{t('ws.insufficient_reasons')}</div>
              <ul className="space-y-1 text-sm text-red-100/85">
                {dq.reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span><span>{r}</span></li>
                ))}
              </ul>
            </div>
          )}
          {(dq.missing || []).length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">{t('ws.insufficient_missing')}</div>
              <div className="flex flex-wrap gap-1.5">
                {dq.missing.map((m, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-[11px] text-white/60">{m}</span>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-white/50 mb-4">{t('ws.insufficient_hint')}</p>
          <button
            onClick={onRetry}
            data-testid="winner-score-retry-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/15 rounded-xl text-sm font-medium transition"
          >
            <ArrowClockwise size={14} weight="bold" />
            {t('ws.insufficient_retry')}
          </button>
        </div>
      </div>
    </div>
  );
};

const DriverList = ({ items, up }) => {
  const { t } = useTranslation();
  return (
    <ul className="space-y-3">
      {(items || []).map((d, i) => (
        <li key={i} className="text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/90 font-medium">{d.factor}</span>
            {d.impact && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ background: `${IMPACT_COLORS[d.impact] || '#9CA3AF'}18`, color: IMPACT_COLORS[d.impact] || '#9CA3AF' }}>
                {t(`ws.impact_${d.impact}`, { defaultValue: d.impact })}
              </span>
            )}
          </div>
          {d.evidence && (
            <div className="mt-1 flex items-start gap-1.5 text-xs text-white/50 italic">
              <Quotes size={12} weight="fill" className="mt-0.5 flex-shrink-0" style={{ color: up ? '#10B981' : '#EF4444' }} />
              <span>{d.evidence}</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export const DriversSection = ({ up, down }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid="winner-score-drivers-up">
        <div className="flex items-center gap-2 mb-4">
          <TrendUp size={18} weight="bold" className="text-[#10B981]" />
          <h3 className="font-[Outfit] font-bold text-base">{t('ws.drivers_up')}</h3>
        </div>
        <DriverList items={up} up />
      </div>
      <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid="winner-score-drivers-down">
        <div className="flex items-center gap-2 mb-4">
          <TrendDown size={18} weight="bold" className="text-[#EF4444]" />
          <h3 className="font-[Outfit] font-bold text-base">{t('ws.drivers_down')}</h3>
        </div>
        <DriverList items={down} up={false} />
      </div>
    </div>
  );
};

export const PriorityActions = ({ items }) => {
  const { t } = useTranslation();
  return (
    <div className="p-6 bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/30 rounded-2xl" data-testid="winner-score-priority-actions">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks size={18} weight="bold" className="text-[#10B981]" />
        <h3 className="font-[Outfit] font-bold">{t('ws.priority_actions')}</h3>
      </div>
      <ol className="space-y-2.5">
        {(items || []).map((a, i) => (
          <li key={i} className="text-sm text-white/80 flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{a}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const ComparisonCard = ({ comparison }) => {
  const { t } = useTranslation();
  const delta = comparison.score_delta || 0;
  const deltaColor = delta > 0 ? '#10B981' : delta < 0 ? '#EF4444' : '#9CA3AF';
  const pc = comparison.page_changes;
  return (
    <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid="winner-score-comparison">
      <div className="flex items-center gap-2 mb-4">
        <ArrowsLeftRight size={18} weight="bold" className="text-[#3B82F6]" />
        <h3 className="font-[Outfit] font-bold">{t('ws.comparison_title')}</h3>
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm text-white/50">{t('ws.comparison_prev')}:</span>
        <span className="font-[Outfit] font-bold text-lg text-white/70">{comparison.previous_score}</span>
        <span
          className="px-2.5 py-1 rounded-full text-xs font-bold border"
          style={{ background: `${deltaColor}18`, color: deltaColor, borderColor: `${deltaColor}55` }}
          data-testid="winner-score-comparison-delta"
        >
          {delta > 0 ? t('ws.comparison_delta_up', { n: delta }) : delta < 0 ? t('ws.comparison_delta_down', { n: Math.abs(delta) }) : t('ws.comparison_same')}
        </span>
        {comparison.previous_created_at && (
          <span className="text-[11px] text-white/40">{new Date(comparison.previous_created_at).toLocaleDateString()}</span>
        )}
      </div>
      {(comparison.indicator_changes || []).length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{t('ws.comparison_ind')}</div>
          <div className="space-y-1.5">
            {comparison.indicator_changes.slice(0, 6).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-white/70 capitalize">{prettify(c.indicator)}</span>
                <span className="font-[Outfit] text-xs">
                  <span className="text-white/40">{c.before}</span>
                  <span className="text-white/30 mx-1.5">→</span>
                  <span style={{ color: c.delta > 0 ? '#10B981' : '#EF4444' }}>{c.after}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pc && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{t('ws.comparison_page')}</div>
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {pc.title && <span className="px-2 py-0.5 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded text-[#93C5FD]">{t('ws.title_changed')}</span>}
            {['h1', 'ctas', 'prices'].map((f) => pc[f] && (
              <React.Fragment key={f}>
                {(pc[f].added || []).map((a, i) => <span key={`a${i}`} className="px-2 py-0.5 bg-[#10B981]/10 border border-[#10B981]/30 rounded text-[#6EE7B7]">+ {a.slice(0, 40)}</span>)}
                {(pc[f].removed || []).map((r, i) => <span key={`r${i}`} className="px-2 py-0.5 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded text-[#FCA5A5]">− {r.slice(0, 40)}</span>)}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ConfChip = ({ confidence }) => {
  const { t } = useTranslation();
  const c = CONF_COLORS[confidence] || '#9CA3AF';
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider flex-shrink-0" style={{ background: `${c}18`, color: c }}>
      {t(`ws.conf_${confidence}`, { defaultValue: confidence })}
    </span>
  );
};

export const IndicatorDetails = ({ details }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  const groups = Object.entries(details || {}).filter(([, v]) => v && typeof v === 'object' && Object.keys(v).length > 0);
  if (!groups.length) return null;
  return (
    <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl" data-testid="winner-score-indicator-details">
      <h3 className="font-[Outfit] font-bold mb-1">{t('ws.details_title')}</h3>
      <p className="text-xs text-white/40 mb-4">{t('ws.details_sub')}</p>
      <div className="space-y-2">
        {groups.map(([group, inds]) => (
          <div key={group} className="border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => setOpen(open === group ? null : group)}
              data-testid={`indicator-group-${group}`}
              className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.03] hover:bg-white/[0.06] transition text-sm font-medium capitalize"
            >
              <span>{t(`ws.axis_${group === 'ia_predictive' ? 'ia' : group}`, { defaultValue: prettify(group) })}</span>
              {open === group ? <CaretUp size={14} /> : <CaretDown size={14} />}
            </button>
            {open === group && (
              <div className="divide-y divide-white/5">
                {Object.entries(inds).map(([key, d]) => (
                  <div key={key} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm text-white/90 capitalize font-medium">{prettify(key)}</span>
                      <div className="flex items-center gap-2">
                        <ConfChip confidence={d?.confidence || 'nao_verificado'} />
                        {typeof d?.score === 'number' && (
                          <span className="font-[Outfit] font-bold text-sm" style={{ color: d.score >= 7 ? '#10B981' : d.score >= 4 ? '#F59E0B' : '#EF4444' }}>
                            {d.score}<span className="text-white/30 text-[10px]">/10</span>
                          </span>
                        )}
                      </div>
                    </div>
                    {d?.evidence && (
                      <div className="text-xs text-white/55 italic mb-1 flex items-start gap-1.5">
                        <Quotes size={11} weight="fill" className="mt-0.5 flex-shrink-0 text-white/30" />
                        <span>{d.evidence}</span>
                      </div>
                    )}
                    {d?.explanation && <p className="text-xs text-white/60 mb-1">{d.explanation}</p>}
                    {d?.recommendation && d.recommendation !== '—' && (
                      <p className="text-xs text-[#93C5FD]"><span className="text-white/40">{t('ws.recommendation')}:</span> {d.recommendation}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const LimitationsCard = ({ items }) => {
  const { t } = useTranslation();
  if (!items?.length) return null;
  return (
    <div className="p-5 bg-white/[0.02] border border-white/10 rounded-2xl" data-testid="winner-score-limitations">
      <div className="flex items-center gap-2 mb-3">
        <Info size={16} weight="bold" className="text-white/50" />
        <h3 className="font-[Outfit] font-bold text-sm text-white/70">{t('ws.limitations_title')}</h3>
      </div>
      <ul className="space-y-1.5 text-xs text-white/50">
        {items.map((l, i) => (
          <li key={i} className="flex items-start gap-2"><span className="mt-0.5">•</span><span>{l}</span></li>
        ))}
      </ul>
    </div>
  );
};

export const HonestyDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <div className="p-5 bg-white/[0.02] border border-white/10 rounded-2xl flex items-start gap-3" data-testid="winner-score-disclaimer">
      <ShieldCheck size={18} weight="bold" className="text-white/40 flex-shrink-0 mt-0.5" />
      <div>
        <div className="text-xs font-semibold text-white/60 mb-1">{t('ws.disclaimer_title')}</div>
        <p className="text-xs text-white/45 leading-relaxed">{t('ws.disclaimer_body')}</p>
      </div>
    </div>
  );
};
